import { createSlice, current, PayloadAction } from '@reduxjs/toolkit'
import { EtypeTokenLoginResponse, ILoginResponse } from '../../../interfaces/auth.interfaces'
import { cachedStorage, eStorageKey } from '../../utils/cachedStorage'
import { RootState } from '../store'

// Define a type for the slice state
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AuthState extends ILoginResponse {}

function persistAuthToken(state: AuthState): void {
  cachedStorage.set(eStorageKey.STATE, {
    authReducer: current(state),
  })
}

// Define the initial state using that type
const initialState: AuthState = {
  type: EtypeTokenLoginResponse.LOGIN,
  common: {
    accepted: [],
  },
  token: cachedStorage.get<RootState>(eStorageKey.STATE)?.authReducer.token || '',
  companyToken: cachedStorage.get<RootState>(eStorageKey.STATE)?.authReducer.companyToken || '',
  user: cachedStorage.get<RootState>(eStorageKey.STATE)?.authReducer.user || {
    companyId: '',
    identity: '',
    roles: [],
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
  },
}

export const authSlice = createSlice({
  name: 'authReducer',
  initialState,
  reducers: {
    setAuthenticatedUser: (state, action: PayloadAction<ILoginResponse>) => {
      state.companyToken = action.payload.companyToken
      state.user = action.payload.user
      state.token = action.payload.token
    },
    setUpdateToken: (state, action: PayloadAction<{ token: string; fromSync?: boolean }>) => {
      if (state.token === action.payload.token) {
        return
      }
      state.token = action.payload.token
      if (!action.payload.fromSync) {
        persistAuthToken(state)
      }
    },
    resetState: (state) => {
      state = { ...initialState, token: '', companyToken: '' }
      cachedStorage.clear(eStorageKey.STATE)
    },
  },
})

export const { setAuthenticatedUser, resetState, setUpdateToken } = authSlice.actions
export default authSlice.reducer
export const selectAuth = (state: RootState): AuthState => state.authReducer

// Selector to get the current user's primary role
export const selectCurrentUserRole = (state: RootState): string => {
  const user = state.authReducer.user
  if (user?.roles && user.roles.length > 0) {
    const role = user.roles[0]
    return typeof role === 'string' ? role : String(role)
  }
  return ''
}
